import React from "react";
import { Skeleton, SkeletonText, Stack } from "@chakra-ui/react";
import { DynamicFormStack } from "../common/ResponsiveFormStack";
import { Trans } from "@lingui/macro";
import { useMealsQuery } from "../../hooks/useMealsQuery";
import { PreviewDropdownFormControl } from "../common/FormControlComponents";
import { useLingui } from "@lingui/react";
import { ActivityChoice } from "../../types/activity.types";
import CurrencyContext from "../../context/CurrencyContext";
import { formatDateForInput } from "../../utils/date-formatter";
import { formatPrice } from "../../utils/price-formatter";
import {
  ModuleChangeStatus,
  ModuleSectionHeader,
} from "../common/ModuleToggleIndicator";

interface MealsProps {
  meals: ActivityChoice[] | null;
  congressId: number;
  setFieldValue: Function;
  isEditMode?: boolean;
  moduleChange?: ModuleChangeStatus;
}

const Meals: React.FC<MealsProps> = ({
  congressId,
  setFieldValue,
  meals,
  isEditMode = false,
  moduleChange = "unchanged",
}) => {
  const { i18n } = useLingui();
  const currency = React.useContext(CurrencyContext);
  const { mealList, areMealsLoading } = useMealsQuery(congressId, isEditMode);

  const isSoftRequired = React.useMemo(
    () => isEditMode && moduleChange === "enabled",
    [isEditMode, moduleChange],
  );
  const hasAnyMealSelected = React.useMemo(
    () =>
      meals != null &&
      Array.isArray(meals) &&
      meals.some((m) => (m?.count ?? 0) > 0),
    [meals],
  );

  React.useEffect(() => {
    if (mealList.length > 0) {
      // Merge existing values with new meal list
      // If meals already has values, preserve them; otherwise initialize with 0
      const activityChoiceList: ActivityChoice[] = mealList.map((meal) => {
        const existing =
          meals && Array.isArray(meals)
            ? meals.find((m) => m.activityId === meal.id)
            : undefined;
        return {
          activityId: meal.id,
          count: existing?.count ?? 0,
        };
      });
      // Only set if the list structure changed (length or IDs)
      const currentMeals = meals && Array.isArray(meals) ? meals : [];
      const needsUpdate =
        currentMeals.length !== activityChoiceList.length ||
        activityChoiceList.some(
          (newMeal) =>
            !currentMeals.some((m) => m.activityId === newMeal.activityId)
        );
      if (needsUpdate) {
        setFieldValue(`meals`, activityChoiceList);
      }
    }
  }, [mealList, setFieldValue]); // Removed meals from dependencies to avoid infinite loop

  return (
    <Stack data-testid="preview-meals-form">
      <ModuleSectionHeader title={<Trans>Meals</Trans>} change={moduleChange} />
      <DynamicFormStack>
        {mealList.map((meal, index) => {
          const existingMeal =
            meals && Array.isArray(meals)
              ? meals.find((m) => m.activityId === meal.id)
              : undefined;
          const count = existingMeal?.count ?? 0;
          const isMealSoftRequired =
            isSoftRequired && !hasAnyMealSelected && count === 0;
          const defaultPreviewText = isMealSoftRequired
            ? i18n._("Originally not selected")
            : undefined;
          return (
            <PreviewDropdownFormControl
              key={index}
              id={`meal${index}`}
              label={`${meal.name} ${formatDateForInput(meal.date)}`}
              isInactive={isEditMode && !meal.enabled}
              values={[
                { id: 0, leftText: i18n._("Not ordered") },
                {
                  id: 1,
                  leftText: i18n._("1 person"),
                  rightText: `${formatPrice(meal.price)} ${currency}`,
                },
                {
                  id: 2,
                  leftText: i18n._("2 person"),
                  rightText: `${formatPrice(meal.price * 2)} ${currency}`,
                },
                {
                  id: 3,
                  leftText: i18n._("3 person"),
                  rightText: `${formatPrice(meal.price * 3)} ${currency}`,
                },
              ]}
              fieldName={`meals[${index}].count`}
              setFieldValue={setFieldValue}
              selectedId={isMealSoftRequired ? undefined : count}
              defaultPreviewText={defaultPreviewText}
              isSoftRequired={isMealSoftRequired}
              testId={`meals-dropdown-${meal.name}`}
            />
          );
        })}
      </DynamicFormStack>
      {areMealsLoading && (
        <>
          <Skeleton height="50px" />
          <SkeletonText mt="4" noOfLines={2} spacing="4" skeletonHeight="2" />
          <Skeleton height="50px" />
        </>
      )}
    </Stack>
  );
};

export default Meals;
